"use client"

import { useState, useMemo } from "react"
import Image from "next/image"
import Link from "next/link"
import {
  ArrowRight,
  CircleDot,
  Disc3,
  Factory,
  Grip,
  Layers,
  Mail,
  Music,
  Phone,
  Search,
  Sparkles,
  Target,
  TreePine,
  Wind,
  Wrench,
  X,
  Zap,
} from "lucide-react"

import { cn } from "@/lib/utils"
import { products, getUniqueBrands } from "@/lib/product-data"
import { categories as categoryData, getCategoryProductCount } from "@/lib/category-data"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AnimateIn } from "@/components/ui/animate-in"
import { CtaBanner } from "@/components/sections/cta-banner"
import { SegmentLinks } from "@/components/sections/segment-links"
import { ProductCard } from "@/components/sections/product-card"

const categoryIcons: Record<string, typeof Disc3> = {
  "Lames circulaires carbure": Disc3,
  "Fers et plaquettes": Layers,
  "Outillages Bois": TreePine,
  "Outillages Divers": Wrench,
  "Électroportatifs": Zap,
  "Rubans": Music,
  "Lames circulaires acier": CircleDot,
  "Fraises scie": Target,
  "Abrasifs": Sparkles,
  "Serrages": Grip,
  "Compresseurs": Wind,
  "Machines d'atelier bois et métal": Factory,
}

const allBrands = getUniqueBrands().sort()
const allCategoryNames = [...new Set(products.map((p) => p.category))].sort()

export default function OutillagePage() {
  const [search, setSearch] = useState("")
  const [selectedBrand, setSelectedBrand] = useState<string>("all")
  const [selectedCategory, setSelectedCategory] = useState<string>("all")
  const [searchFocused, setSearchFocused] = useState(false)

  const filtered = useMemo(() => {
    let result = products

    if (selectedCategory !== "all") {
      result = result.filter((p) => p.category === selectedCategory)
    }
    if (selectedBrand !== "all") {
      result = result.filter((p) => p.brand === selectedBrand)
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim()
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.brand.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q)
      )
    }
    return result
  }, [search, selectedBrand, selectedCategory])

  const hasFilters = search.trim() !== "" || selectedBrand !== "all" || selectedCategory !== "all"

  function clearFilters() {
    setSearch("")
    setSelectedBrand("all")
    setSelectedCategory("all")
  }

  return (
    <>
      {/* ── HERO ──────────────────────────────────────── */}
      <section className="relative min-h-[50vh] overflow-hidden">
        <Image
          src="/images/remote/conseil-outillage.jpg"
          alt="Showroom outillage professionnel ADIS 95 — Festool Test Center"
          fill
          className="object-cover"
          sizes="100vw"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        <div className="relative mx-auto flex min-h-[50vh] max-w-7xl items-end px-4 pb-12 md:px-6 md:pb-20">
          <div className="max-w-2xl">
            <Badge className="mb-4 bg-primary/90 text-primary-foreground hover:bg-primary">
              Outillage
            </Badge>
            <h1 className="text-3xl font-bold tracking-tight text-white md:text-5xl">
              Nos produits : Marques de qualité, Marques de confiance.
            </h1>
            <p className="mt-4 max-w-[55ch] text-lg leading-relaxed text-white/80">
              Pour travailler efficacement, en sécurité et avec la plus grande qualité possible.
            </p>
          </div>
        </div>
      </section>

      {/* ── FESTOOL PREMIUM PARTNER ───────────── */}
      <section className="section-tight px-4 md:px-6">
        <div className="mx-auto max-w-7xl">
          <AnimateIn>
            <div className="grid grid-cols-1 items-center gap-8 overflow-hidden rounded-3xl bg-primary/[0.04] ring-2 ring-primary/20 lg:grid-cols-5">
              <div className="relative h-64 lg:col-span-2 lg:h-full lg:min-h-[320px]">
                <Image
                  src="/images/remote/outils-stand-1024x467.jpg"
                  alt="Showroom Festool Test Center ADIS 95"
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 40vw"
                />
              </div>
              <div className="px-8 pb-8 pt-2 lg:col-span-3 lg:py-10 lg:pr-10">
                <div className="flex items-center gap-3">
                  <Image src="/images/brands/festool.svg" alt="Logo Festool" width={56} height={24} className="h-6 max-w-[56px] rounded object-contain" />
                  <div className="flex gap-2">
                    <Badge className="bg-primary text-primary-foreground">Partenaire Premium</Badge>
                    <Badge variant="outline" className="border-primary text-primary">Test Center</Badge>
                  </div>
                </div>
                <h2 className="mt-5 text-2xl font-bold tracking-tight md:text-3xl">
                  Revendeur agréé Festool
                </h2>
                <p className="mt-3 max-w-[55ch] text-base leading-relaxed text-muted-foreground">
                  ADIS 95 est fier d&apos;être partenaire premium Festool. Retrouvez chez nous toute la gamme d&apos;électroportatifs Festool, avec le conseil expert de notre équipe.
                </p>
                <p className="mt-3 max-w-[55ch] text-base leading-relaxed text-muted-foreground">
                  Nous vous apportons le conseil et un large choix de matériel répondant à chacun de vos besoins, nous nous appuyons pour cela sur notre longue expérience dans le domaine de l&apos;outillage spécialisé pour le travail du bois, ainsi que sur l&apos;expertise de nos fabricants.
                </p>
                <Button asChild className="group mt-6 h-11 gap-2 rounded-full pl-5 pr-2 active:scale-[0.97]">
                  <Link href="/marque/festool">
                    Voir la gamme Festool
                    <span className="flex size-7 items-center justify-center rounded-full bg-primary-foreground/20 transition-transform duration-200 group-hover:translate-x-0.5">
                      <ArrowRight className="size-3.5" />
                    </span>
                  </Link>
                </Button>
              </div>
            </div>
          </AnimateIn>
        </div>
      </section>

      {/* ── PRODUCT EXPLORER with Tabs ──────── */}
      <section className="section-normal px-4 md:px-6" id="produits">
        <div className="mx-auto max-w-7xl">
          <AnimateIn>
            <Badge variant="secondary" className="mb-4 text-xs uppercase tracking-[0.15em]">
              Catalogue
            </Badge>
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
              Explorer nos produits
            </h2>
            <p className="mt-3 max-w-[55ch] text-base text-muted-foreground">
              {products.length} produits, {allBrands.length} marques, {allCategoryNames.length} catégories.
            </p>
          </AnimateIn>

          <Tabs defaultValue="products" className="mt-8">
            <TabsList className="mb-6">
              <TabsTrigger value="products">Tous les produits</TabsTrigger>
              <TabsTrigger value="categories">Par catégorie</TabsTrigger>
            </TabsList>

            {/* ── TAB: All products ──────── */}
            <TabsContent value="products">
              {/* Search + Select filters row */}
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                {/* Search */}
                <div
                  className={cn(
                    "relative flex flex-1 items-center rounded-full border bg-muted/30 transition-[transform,opacity,box-shadow,ring-color,border-color,background-color,color] duration-300",
                    searchFocused
                      ? "border-primary/40 ring-2 ring-primary/10 bg-background"
                      : "border-border"
                  )}
                >
                  <Search
                    className="ml-4 size-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                    placeholder="Rechercher un produit, une marque..."
                    aria-label="Rechercher un produit ou une marque"
                    className="w-full bg-transparent px-3 py-2.5 text-sm outline-hidden focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:rounded-full placeholder:text-muted-foreground/60"
                  />
                  {search && (
                    <button
                      onClick={() => setSearch("")}
                      className="mr-3 rounded-full p-0.5 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="Effacer la recherche"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </div>

                {/* Category filter — native <select>. Previously used
                 * Shadcn/Radix `<Select>`, which renders a visually-
                 * nice button + a hidden <select aria-hidden> for
                 * form fallback. That hidden select was the ROOT of
                 * 5 accessibility failures the squirrel audit flagged
                 * on every audit pass (select-name, aria-hidden-focus,
                 * aria-input-field-name, form-labels, duplicate-id-aria)
                 * because the hidden <select> is focusable (tabindex=-1
                 * doesn't fully remove focus per squirrel) and gets no
                 * accessible name. Swapping to a native <select> with
                 * a visible-but-sr-only <label> kills all five rules
                 * at once and keeps the filter behavior identical. */}
                <label className="sr-only" htmlFor="filter-category">
                  Filtrer par catégorie de produit
                </label>
                <select
                  id="filter-category"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full sm:w-52 rounded-full border border-transparent bg-input/50 px-4 py-2 text-sm outline-hidden transition-[color,box-shadow,background-color] focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 h-9"
                >
                  <option value="all">Toutes catégories</option>
                  {allCategoryNames.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>

                <label className="sr-only" htmlFor="filter-brand">
                  Filtrer par marque
                </label>
                <select
                  id="filter-brand"
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  className="w-full sm:w-48 rounded-full border border-transparent bg-input/50 px-4 py-2 text-sm outline-hidden transition-[color,box-shadow,background-color] focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 h-9"
                >
                  <option value="all">Toutes marques</option>
                  {allBrands.map((brand) => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>

              {/* Active filter summary */}
              {hasFilters && (
                <div className="mt-4 flex items-center gap-3">
                  <p className="text-sm text-muted-foreground">
                    {filtered.length} produit{filtered.length !== 1 ? "s" : ""} trouvé{filtered.length !== 1 ? "s" : ""}
                  </p>
                  <button
                    onClick={clearFilters}
                    className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                  >
                    Réinitialiser
                  </button>
                </div>
              )}

              {/* Product grid */}
              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filtered.map((product) => (
                  <ProductCard key={product.slug} product={product} />
                ))}
              </div>

              {filtered.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Search className="mb-4 size-10 text-muted-foreground/40" />
                  <p className="text-lg font-medium text-muted-foreground">Aucun produit trouvé</p>
                  <Button variant="secondary" className="mt-4" onClick={clearFilters}>
                    Réinitialiser les filtres
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* ── TAB: By category ──────── */}
            <TabsContent value="categories">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {categoryData.map((category) => {
                  const Icon = categoryIcons[category.name] || Wrench
                  const productCount = getCategoryProductCount(category.slug)
                  return (
                    <AnimateIn key={category.slug}>
                      <Link
                        href={`/categorie-produit/${category.slug}`}
                        className="group block h-full"
                      >
                        <div
                          className={cn(
                            "h-full rounded-[2rem] p-1.5",
                            "bg-foreground/[0.02] ring-1 ring-foreground/5",
                            "dark:bg-background/[0.06] dark:ring-foreground/10",
                            "transition-[ring-color,box-shadow] duration-200 hover:ring-foreground/15 hover:shadow-[var(--shadow-md)]"
                          )}
                        >
                          <div className="flex h-full flex-col rounded-[calc(2rem-0.375rem)] bg-card p-6">
                            <div className="flex items-center justify-between">
                              <Icon className="size-6 text-primary" />
                              <Badge variant="secondary" className="text-xs">
                                {productCount}{" "}
                                {productCount > 1 ? "produits" : "produit"}
                              </Badge>
                            </div>
                            <h3 className="mt-4 text-lg font-semibold tracking-tight group-hover:text-primary transition-colors duration-200">
                              {category.name}
                            </h3>
                            <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                              {category.heading}
                            </p>
                            <div className="mt-auto pt-4 flex items-center justify-between">
                              <Badge variant="outline" className="text-xs">
                                {category.brands.length}{" "}
                                {category.brands.length > 1 ? "marques" : "marque"}
                              </Badge>
                              <ArrowRight className="size-4 text-primary opacity-0 transition-[transform,opacity] duration-200 group-hover:translate-x-0.5 group-hover:opacity-100" />
                            </div>
                          </div>
                        </div>
                      </Link>
                    </AnimateIn>
                  )
                })}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* ── CONTACT CTA ──────────────────────── */}
      <section className="section-normal px-4 md:px-6">
        <div className="mx-auto max-w-7xl text-center">
          <AnimateIn>
            <Badge variant="secondary" className="mb-4 text-xs uppercase tracking-[0.15em]">
              Contact
            </Badge>
            <h2 className="text-2xl font-bold tracking-tight md:text-4xl">
              {"Besoin d'info, n'hésitez pas à…"}
            </h2>
            <p className="mx-auto mt-3 max-w-[50ch] text-base text-muted-foreground">
              {"Notre équipe d'experts est prête à répondre à tous vos besoins, chez vous ou au comptoir."}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="group h-12 gap-3 rounded-full pl-6 pr-2 active:scale-[0.97]">
                <Link href="/contact">
                  <Mail className="size-4" />
                  <span>Nous contacter</span>
                  <span className="flex size-8 items-center justify-center rounded-full bg-primary-foreground/20 transition-transform duration-200 group-hover:translate-x-0.5">
                    <ArrowRight className="size-4" />
                  </span>
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-12 rounded-full active:scale-[0.97]">
                <a href="tel:+33130370530">
                  <Phone className="mr-2 size-4" />
                  +33 (0)1 30 37 05 30
                </a>
              </Button>
            </div>
          </AnimateIn>
        </div>
      </section>

      <SegmentLinks />
      <CtaBanner />
    </>
  )
}
